import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { TableRow } from 'Src/feature/data-table/TableRow';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { UsedModalSearchParamControl } from 'Src/feature/modal/useModalControl';

import PayInOutDetails from './PayInOutDetails';
import { usePayInOut } from './usePayInOut';
import { Field, Form, usePayInOutList } from './usePayInOutList';

const sectionId = 'PayInOutList';

interface PayInOutListProps {
  payInOutListModal: UsedModalSearchParamControl;
}

const PayInOutList: FC<PayInOutListProps> = ({
  payInOutListModal,
}) => {
  const { t } = useTranslation();
  const { data: payInOutReasonMap } = usePayInOut.query.reason.map();
  const store = usePayInOutList(payInOutListModal);
  const { form } = store;
  const { Table, Column } = store.table;

  return (
    <>
      <Modal
        variant="fullScreen"
        data-testid={`${sectionId}-modal-payInOutLog`}
        open={payInOutListModal.isOpen}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.onClose} />}>
          <ModalTitle copy={t('payInOut.labels.payInOutLog')} />
        </ModalHeader>

        <ModalContent>
          <Form usedForm={form} layout={['grid', 'col3']} omitChangeTracking >
            <Field
              name="isPayIn"
              type="select"
              variant="contained"
              fullWidth
              options={store.options.types}
              onAfterChange={({ value }) => store.onFilter('isPayIn', value)}
            />
            <Field
              name="paymentMethodId"
              type="select"
              variant="contained"
              fullWidth
              options={store.options.paymentMethods}
              onAfterChange={({ value }) => store.onFilter('paymentMethodId', value)}
            />
            <Field
              name="employeeId"
              type="select"
              variant="contained"
              fullWidth
              options={store.options.employees}
              onAfterChange={({ value }) => store.onFilter('employeeId', value)}
            />
          </Form>
          <Table
            pagination={store.table.pagination}
            noDataMessage={t('payInOut.errors.getAll_noData')}
            renderer={{
              row: (dataItem) => ({ children }) => (
                <TableRow
                  onClick={() => {
                    store.modal.details.open();
                    store.setPayInOutTxDetails(dataItem);
                  }}
                >
                  {children}
                </TableRow>
              )
            }}
          >
            <Column
              width={150}
              label={t('labels.type')}
              field="isPayIn"
              cellContentRenderer={({ dataItem: { isPayIn } }) => {
                const color = isPayIn ? 'success' : 'error';
                const label = isPayIn ? t('payInOut.labels.payIn') : t('payInOut.labels.payOut');
                return (
                  <Flex horizontal gap="xs" alignItems="center">
                    <FontIcon name="fiber_manual_record" color={color} />
                    <Copy value={label} />
                  </Flex>
                );
              }}
            />
            <Column
              width={150}
              label={t('labels.time')}
              sortable
              field="createdAt"
              cellContentRenderer={({ dataItem: { createdAt } }) => <Copy value={createdAt ? DateTime.fromMillis(createdAt).toLocaleString(DateTime.TIME_SIMPLE) : ''} />}
            />
            <Column
              width={125}
              label={t('labels.amount')}
              field="amount"
              cellContentRenderer={({ value }) => <Copy type="currency" ellipsis value={value} />}
            />
            <Column
              minWidth={180}
              label={t('employee.label')}
              field="employeeId"
              cellContentRenderer={({ value }) => {
                const employee = value ? store.employeeMap?.get(value) : undefined;
                return <Copy ellipsis optional value={employee ? t('labels.util.employeeFullName', employee) : undefined} />;
              }}
            />
            <Column
              minWidth={150}
              label={t('payInOut.labels.paymentMethod')}
              field="paymentMethodId"
              cellContentRenderer={({ value }) => <Copy value={store.paymentMethodMap?.get(value)?.name} />}
            />
            <Column
              minWidth={200}
              label={t('payInOut.labels.reasonSlashNotes')}
              cellContentRenderer={({ dataItem: { payInOutReasonId, notes } }) => (
                <Flex gap="xs" alignItems="start" justifyContent="center">
                  <Copy variant="body" value={payInOutReasonMap?.get(payInOutReasonId)?.name} />
                  <Copy variant="body" color="textSecondary" value={notes} />
                </Flex>
              )}
            />
            <Column
              minWidth={150}
              label={t('labels.status')}
              cellContentRenderer={({ dataItem: { isVoid, status } }) => (
                <Copy value={isVoid ? t('labels.voided') : status} />
              )}
            />
          </Table>
        </ModalContent>
      </Modal>
      <PayInOutDetails payInOutDetailsModal={store.modal.details} payInOutTx={store.payInOutTxDetails} />
    </>
  );
};

export type { PayInOutListProps };
export { PayInOutList, sectionId };
